/**
 * One source of truth for the plain-English meaning of every term on the
 * Program News admin page. Wording matches panelIntros.ts and the source
 * legend. "Failed" means three different things, so it has three keys.
 */
import type {
  ProgramNewsItemStatus,
  ProgramNewsRunStatus,
  ProgramSourceStatus,
} from "@/services/adminProgramNews";

export const GLOSSARY = {
  // Article statuses
  new: "Filed by the collector and waiting for the classifier to read it.",
  suppressed:
    "Thrown out by a simple rule before the AI read it. Routine things like round-by-round scores, previews and honor rolls never become events.",
  classified: "The AI read the article and sorted it as an alert, a digest item or ignore.",
  failed:
    "The AI call for this article errored, so it was not sorted. A person should look at the error.",
  // Event classes
  alert:
    "Time-sensitive change worth telling a family right away, such as a coach change or a signing. Only kept when the article text confirms it.",
  digest: "Useful weekly context, such as schedules, postseason bids and major results.",
  ignore:
    "The AI read it and decided a family would not need to hear about it, or a headline looked important but the article text did not back it up.",
  // Event types
  "coach-change": "A head or assistant coach was hired, promoted, fired or left.",
  "player-signing": "A player signed or committed to the program.",
  schedule: "A schedule, tournament field or calendar announcement.",
  postseason: "A postseason bid, pairing or result (conference, regional or national).",
  "major-result": "A notable team or player result, such as a tournament win.",
  "major-award": "A notable award or honor, such as all-American or player of the year.",
  "program-change": "A change to the program itself, such as facilities, staffing or status.",
  other: "An event that does not fit any of the other types.",
  // Source statuses
  verified: "We found this program's news feed and it returned golf stories. Nothing to do.",
  queued:
    "We found the school's athletics site but no golf news feed we recognize. Usually a platform we don't support yet.",
  source_failed:
    "We couldn't find an athletics website for this college at all. Set an override with the athletics site's home page.",
  disabled:
    "Turned off on purpose. Not collected. Disable a source that will never have golf news.",
  override_stale:
    "Someone set a URL for this program and it stopped working; the crawl found a possible replacement. Review the suggestion.",
  on_list:
    "At least one athlete on someone's recruiting list is interested in this program, so it is shown first.",
  // Reviews
  reviewed:
    "You recorded whether the pipeline made the right call on this one. Your verdicts add up to the accuracy numbers.",
  // Runs
  running: "This step is in progress right now. The row updates while it runs.",
  succeeded: "This step finished without an error.",
  run_failed: "This step stopped with an error. The error text is in the last column.",
  full_run:
    "Not capped: this run worked through every verified source or every waiting article, the same as the nightly job.",
  collect: "Visits each verified news feed and files any new articles.",
  classify: "Reads new articles, drops routine noise with rules, and sorts the rest with the AI.",
} as const;

export type GlossaryKey = keyof typeof GLOSSARY;

export function articleStatusTerm(status: ProgramNewsItemStatus): GlossaryKey {
  return status;
}

export function sourceStatusTerm(status: ProgramSourceStatus): GlossaryKey {
  return status === "failed" ? "source_failed" : status;
}

export function runStatusTerm(status: ProgramNewsRunStatus): GlossaryKey {
  return status === "failed" ? "run_failed" : status;
}

export const REASON_NOT_RECORDED = "Reason not recorded (sorted before reasons were saved)";
